import { useMemo, useState } from 'react';
import { FaMagnifyingGlass } from 'react-icons/fa6';

import { Grid2, Pagination } from '@mui/material';

import { DEFAULT_PAGE_SIZE } from 'constants/general';
import { Input, Table, TableBody, TableHead } from 'shared';
import { Contracts, Invoices } from 'types/api/profile.types';
import { TableDataMap, TableHeaderOptions } from 'types/components/table.types';
import { TransactionBitcoin, TransactionUSD } from 'types/components/wallet.types';
import { SortType } from 'types/util.types';

type Props<T extends TransactionUSD | TransactionBitcoin | Invoices | Contracts> = {
  transactions: T[] | null;
  transactionMap: TableDataMap<T>;
  headerOptions: TableHeaderOptions<T>[];
  pagination?: boolean;
  isDownloadRow?: boolean;
  isLoading?: boolean;
  onRowAction?: (item: T) => void;
  RowActionElement?: React.ReactNode;
};

const TransactionsTable = <T extends TransactionUSD | TransactionBitcoin | Invoices | Contracts>({
  transactions,
  headerOptions,
  transactionMap,
  onRowAction,
  RowActionElement,
  pagination = true,
  isDownloadRow = false,
  isLoading = false,
}: Props<T>) => {
  const [sort, setSort] = useState<{ key: keyof T; sortType: SortType } | null>({
    key: 'date' as keyof T,
    sortType: SortType.DESCENDING,
  });
  const [filter, setFilter] = useState<string>('');
  const [page, setPage] = useState<number>(1);

  const onSort = (key: keyof T) => {
    const type = sort?.sortType && sort.sortType !== SortType.DESCENDING ? SortType.DESCENDING : SortType.ASCENDING;
    setSort({ key, sortType: type });
  };

  const paginatedValues = useMemo(() => {
    const filtered =
      filter.length > 0
        ? transactions?.filter(v =>
            Object.values(v).find(i => i?.toString().toLowerCase().includes(filter.toLowerCase())),
          )
        : transactions;

    if (!filtered) return [];

    const sorted = sort
      ? [...filtered].sort((trA, trB) => {
          const valA = trA[sort.key];
          const valB = trB[sort.key];

          if (!valA) return 1;
          if (!valB) return -1;

          const isDate = typeof valA === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(valA);
          const isCurrency = typeof valA === 'string' && /^[\d,.]+\u00A0?€$/.test(valA);

          if (isDate) {
            const parseDate = (str: string) => {
              const [day, month, year] = str.split('/').map(Number);
              return new Date(year, month - 1, day);
            };

            const dateA = parseDate(valA as string);
            const dateB = parseDate(valB as string);

            return sort.sortType === SortType.ASCENDING
              ? dateA.getTime() - dateB.getTime()
              : dateB.getTime() - dateA.getTime();
          }

          if (isCurrency) {
            const parseCurrency = (str: string) => parseFloat(str.replace(/[^\d,]/g, '').replace(',', '.'));

            const numA = parseCurrency(valA as string);
            const numB = parseCurrency(valB as string);

            return sort.sortType === SortType.ASCENDING ? numA - numB : numB - numA;
          }

          if (sort.sortType === SortType.ASCENDING) {
            if (valA > valB) return 1;
            if (valA < valB) return -1;
            return 0;
          } else {
            if (valA < valB) return 1;
            if (valA > valB) return -1;
            return 0;
          }
        })
      : filtered;

    const startIndex = (page - 1) * DEFAULT_PAGE_SIZE;
    const endIndex = startIndex + DEFAULT_PAGE_SIZE;
    return sorted.slice(startIndex, endIndex);
  }, [filter, sort, transactions, page]);

  const totalPages = useMemo(() => {
    const filtered =
      filter.length > 0
        ? transactions?.filter(v =>
            Object.values(v).find(i => i?.toString().toLowerCase().includes(filter.toLowerCase())),
          )
        : transactions;

    return Math.ceil((filtered?.length ?? 0) / DEFAULT_PAGE_SIZE);
  }, [filter, transactions]);

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={12} display={'flex'} justifyContent={'end'}>
        <Input
          placeholder='Search...'
          value={filter}
          endIcon={<FaMagnifyingGlass size={24} />}
          onChange={e => {
            setFilter(e.target.value);
            setPage(1);
          }}
          testId='transactions-search'
        />
      </Grid2>
      <Grid2 size={12}>
        <Table>
          <TableHead isLoading={isLoading} onSort={onSort} headerOptions={headerOptions} />
          <TableBody
            values={paginatedValues}
            transformValueMapper={transactionMap}
            onRowAction={onRowAction}
            isLoading={isLoading}
            loadingCount={headerOptions.length + (isDownloadRow ? 1 : 0)}
            RowActionElement={RowActionElement}
          />
        </Table>
        {pagination && (
          <Grid2 container justifyContent='center' sx={{ mt: 2 }}>
            <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} color='primary' />
          </Grid2>
        )}
      </Grid2>
    </Grid2>
  );
};

export default TransactionsTable;
